import cn from 'classnames';
import Immutable from 'immutable';
import PropTypes from 'prop-types';
import React from 'react';
import loadable from '@loadable/component';
import { FormattedMessage, injectIntl } from 'react-intl';

import { bibShape, libraryShape, userIdentityShape } from '@bibliocommons/bc-prop-types';
import { Flag } from '@bibliocommons/deprecated-base-icons';
import FormatIcon from '@bibliocommons/format-icon';
import { bindAll } from '@bibliocommons/utils-react';
import * as Icons from '@bibliocommons/deprecated-base-icons';
import manageFocus from '@bibliocommons/base-accessibility-manage-focus';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { TextButton } from '@bibliocommons/deprecated-base-buttons';

import AddToListDropdown from 'app/components/shared/displayBib/AddToListDropdown';
import { THREE_LETTER_TO_TWO_LETTER_LANG_CODES } from 'app/constants/LanguageConstants';
import { Generic } from 'app/components/shared/GenericMessage';
import AddToShelfComboButtonContainer from 'app/components/deprecated/shared/AddToShelfComboButton/AddToShelfComboButtonContainer';
import MessagingContainer from 'app/components/Messaging/MessagingContainer';
import SearchAnalyticsContext from 'app/components/search/SearchAnalyticsContext';
import { getPropsForFormatInfo } from 'app/helpers/bib/bibHelpers';
import { mapSearchBibAvailabilityToDisplayAvailability, mapSearchBibToDisplayBib } from 'app/helpers/displayBib';
import { isStaff } from 'app/helpers/user';
import AvailabilityBibBlock from 'app/components/shared/AvailabilityBibBlock';
import FormatInfo from 'app/components/shared/FormatInfo';
import { catalogSearchOptionsShape } from 'app/shapes/search';
import { ShelfMessage } from '../../shared/Messages';
import GroupingSummary from '../GroupingSummary';
import ItemTransactionButtonContainer from '../../shared/ItemTransactionButton/ItemTransactionButtonContainer';

import './ManifestationListItem.scss';

const WorkflowContainer = loadable(() => import('app/components/Workflow/WorkflowContainer'));

class ManifestationListItem extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
    this.state = {
      transactionButtonToggleOpen: this.props.drawerOpen || false
    };
  }

  componentDidUpdate(_prevProps, prevState) {
    if (this.state.transactionButtonToggleOpen && !prevState.transactionButtonToggleOpen) {
      this.props.captureFocus(this._actionDrawer, false);
    } else {
      this.props.restoreFocus();
    }
  }

  getManifestationText() {
    const { intl, item, library, searchScopeIsCurrent } = this.props;

    const { callNumber, edition, formatMessageId, languageMessageId, publicationDate } = getPropsForFormatInfo({
      bib: item?.toJS(),
      library: library?.toJS(),
      searchScopeIsCurrent
    });

    return [
      formatMessageId && intl.formatMessage({ id: formatMessageId }),
      publicationDate && `- ${publicationDate}`,
      languageMessageId && `| ${intl.formatMessage({ id: languageMessageId })}`,
      edition,
      callNumber
    ]
      .filter(Boolean)
      .join(' ');
  }

  handleToggleClick(e) {
    e.preventDefault();

    return this.setState(state => ({
      transactionButtonToggleOpen: !state.transactionButtonToggleOpen
    }));
  }

  renderGroupSummary(item) {
    const { catalogSearchOptions, showGroupingDebug, startGroupSearchFeedback } = this.props;

    if (catalogSearchOptions.get('showGroupings') && showGroupingDebug) {
      return (
        <div className="manifestation-item-debug-actions hidden-xs">
          <GroupingSummary groupSummary={item.get('groupSearchSummary')} />
          <TextButton className="cp-group-search-feedback-button" handleClick={() => startGroupSearchFeedback(item)}>
            <ScreenReaderMessage>
              <FormattedMessage id="group_search_feedback" />
            </ScreenReaderMessage>
            <Flag />
          </TextButton>
        </div>
      );
    }
    return null;
  }

  renderManifestationItemToggle() {
    const { transactionButtonToggleOpen } = this.state;
    const { editable } = this.props;
    const itemId = this.props.item.get('id');
    const toggleIcon = editable ? (
      <Icons.PencilEdit width={20} height={20} color="#02729e" />
    ) : (
      <Icons.More width={20} height={20} color="#02729e" />
    );

    return (
      <div
        className={cn('manifestation-item-action-toggle', 'hidden-sm hidden-md hidden-lg', {
          'visible-xs toggled-open': transactionButtonToggleOpen
        })}
      >
        <TextButton
          handleClick={this.handleToggleClick}
          id={`manifestation-item-action-wrap-${itemId}`}
          aria-expanded={transactionButtonToggleOpen}
        >
          {toggleIcon}
          <ScreenReaderMessage>
            <FormattedMessage id="ada_toggle_manifestation_item_actions" />
          </ScreenReaderMessage>
        </TextButton>
      </div>
    );
  }

  render() {
    const {
      currentUser,
      getDigitalAvailability,
      item,
      itemPosition,
      itemPositionGrouped,
      totalCount,
      library,
      openAvailability,
      reciprocalRank,
      searchScopeIsCurrent,
      showHoldCount
    } = this.props;

    const {
      trackAddToListClick,
      trackAddToNewListClick,
      trackViewAvailabilityClick,
      trackViewDigitalResourceClick,
      trackViewManifestationClick
    } = this.context;

    const { transactionButtonToggleOpen } = this.state;
    const itemId = item.get('id');
    const bibTitle = item.getIn(['briefInfo', 'title']);
    const statusType = item.getIn(['availability', 'statusType']);

    const displayAvailability = mapSearchBibAvailabilityToDisplayAvailability(item.get('availability')?.toJS());
    const displayBib = mapSearchBibToDisplayBib(item?.toJS());

    const trackClick = () =>
      trackViewManifestationClick({
        bib: item,
        linkText: this.getManifestationText(),
        itemPosition,
        itemPositionGrouped,
        totalCount
      });

    return (
      <WorkflowContainer
        id={itemId}
        reciprocalRank={reciprocalRank}
        itemPosition={itemPosition}
        itemPositionGrouped={itemPositionGrouped}
        totalCount={totalCount}
      >
        {workflowComponent =>
          workflowComponent || (
            <div className="manifestation-item cp-manifestation-list-item row" key={itemId}>
              <MessagingContainer id={itemId}>
                {(messageOptions, dispatch) =>
                  messageOptions.message.get('type') === 'SHELF' ? (
                    <ShelfMessage
                      {...messageOptions}
                      dispatch={dispatch}
                      values={messageOptions.message.get('values', Immutable.Map()).merge({
                        title: bibTitle,
                        username: this.props.currentUser.get('name')
                      })}
                    />
                  ) : (
                    <Generic
                      {...messageOptions}
                      values={messageOptions.message.get('values', Immutable.Map()).merge({ title: bibTitle })}
                    />
                  )}
              </MessagingContainer>
              <div className={`manifestation-item-format-call-wrap ${statusType.toLowerCase()}`}>
                <div className="manifestation-item-format-call-wrap-inner">
                  <FormatIcon
                    aria-hidden="true"
                    className="icon"
                    format={item.getIn(['briefInfo', 'format'])}
                  ></FormatIcon>
                  <div className="manifestation-item-format-info-wrap">
                    <a
                      className="manifestation-item-link"
                      href={`/v2/record/${itemId}`}
                      target="_parent"
                      lang={THREE_LETTER_TO_TWO_LETTER_LANG_CODES[item.get('primaryLanguage')] || 'en'}
                      rel="noopener"
                      data-test-id={`item-link-${itemId}`}
                      data-key="bib-manifestation-item-link"
                      onAuxClick={trackClick}
                      onClick={trackClick}
                    >
                      <FormatInfo
                        {...getPropsForFormatInfo({
                          bib: item?.toJS(),
                          library: library?.toJS(),
                          searchScopeIsCurrent
                        })}
                      />
                    </a>
                    <div className="manifestation-item-availability-block-wrap">
                      <AvailabilityBibBlock
                        bib={item}
                        fetchDigitalAvailability={getDigitalAvailability}
                        hasAvailabilityStatus
                        hasFetchDigitalAvailabilityButton
                        hasHoldCounts={showHoldCount}
                        hasPhysicalAvailabilityLink
                        itemPosition={itemPosition}
                        itemPositionGrouped={itemPositionGrouped}
                        openAvailabilityOverlay={openAvailability}
                        totalCount={totalCount}
                        trackViewAvailabilityClick={trackViewAvailabilityClick}
                        trackViewDigitalResourceClick={trackViewDigitalResourceClick}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {this.renderManifestationItemToggle()}
              <div
                className={`manifestation-item-action-wrap ${transactionButtonToggleOpen ? 'toggled-open' : ''}`}
                ref={el => {
                  this._actionDrawer = el;
                }}
              >
                <ItemTransactionButtonContainer
                  id={itemId}
                  messagingId={itemId}
                  itemPosition={itemPosition}
                  itemPositionGrouped={itemPositionGrouped}
                  totalCount={totalCount}
                />

                <div className={cn('add-to-shelf-wrap', { 'add-to-shelf-wrap--staff': isStaff(currentUser) })}>
                  <AddToShelfComboButtonContainer
                    metadataId={itemId}
                    reciprocalRank={reciprocalRank}
                    itemPosition={itemPosition}
                    itemPositionGrouped={itemPositionGrouped}
                    totalCount={totalCount}
                  />
                </div>
                {isStaff(currentUser) && (
                  <AddToListDropdown
                    className="add-to-list-dropdown"
                    displayAvailability={displayAvailability}
                    displayBib={displayBib}
                    trackAddToListClick={trackAddToListClick}
                    trackAddToNewListClick={trackAddToNewListClick}
                  />
                )}

                {this.renderGroupSummary(item)}
              </div>
            </div>
          )}
      </WorkflowContainer>
    );
  }
}

ManifestationListItem.contextType = SearchAnalyticsContext;

ManifestationListItem.propTypes = {
  catalogSearchOptions: catalogSearchOptionsShape.isRequired,
  currentUser: userIdentityShape,
  item: bibShape.isRequired,
  itemPosition: PropTypes.number,
  itemPositionGrouped: PropTypes.number,
  totalCount: PropTypes.number,
  drawerOpen: PropTypes.bool,
  showHoldCount: PropTypes.bool,
  reciprocalRank: PropTypes.number,
  showGroupingDebug: PropTypes.bool,
  editable: PropTypes.bool,
  library: libraryShape.isRequired,
  getDigitalAvailability: PropTypes.func.isRequired,
  openAvailability: PropTypes.func.isRequired,
  searchScopeIsCurrent: PropTypes.bool,
  restoreFocus: PropTypes.func,
  captureFocus: PropTypes.func,
  startGroupSearchFeedback: PropTypes.func.isRequired
};

export default manageFocus(injectIntl(ManifestationListItem));
